import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { CatalogMetadata } from '@bounded/api-client';
import { validCatalogMetadata, type CatalogAgent } from '../../../../../packages/protocol/src/catalog-metadata';
import { ConfigEditor, isObject } from '@bounded/ui/config-editor';
import { api } from '../../api/client';
import { q } from '../../api/queries';
import { useMe } from '../_app';
import { ReasonAction } from '../../components/reason-action';
import { JsonArea, parseJson } from '../../components/json-area';
import { Empty, Field, Input, Mono, Page, PageHeader, Panel, Skeleton } from '@bounded/ui/primitives';
import { ErrorNotice } from '@bounded/ui/state';
import { Setting } from '../../components/setting';
import { useFormat, useT } from '../../i18n';

export const Route = createFileRoute('/_app/releases')({ component: Releases });

function Releases() {
  const t = useT(); const m = t.releases; const f = useFormat();
  const me = useMe();
  const queryClient = useQueryClient();
  const releases = useQuery(q.releases());
  const refresh = () => Promise.all(['releases', 'audit'].map((k) => queryClient.invalidateQueries({ queryKey: [k] })));
  const items = [...(releases.data?.items ?? [])].sort((a, b) => b.created_at - a.created_at);
  return (
    <Page>
      <PageHeader title={m.title} description={m.description} />
      <p className="max-w-[72ch] rounded-[var(--radius-control)] border border-rule bg-surface px-3 py-2.5">{m.rule}</p>
      {me.permissions.mutate && <PublishForm onDone={refresh} />}
      {releases.error ? <ErrorNotice error={releases.error} onRetry={() => void releases.refetch()} /> : (
        <Panel>
          {!releases.data ? <div className="p-4"><Skeleton className="h-32" /></div> : !items.length ? <Empty title={m.empty} /> : (
            <ul>{items.map((r) => (
              <li key={r.id} className="grid gap-3 border-b border-rule px-4 py-3 last:border-b-0">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="grid min-w-0 gap-0.5">
                    <p className="font-medium">{r.metadata?.title ?? r.id} <span className="font-normal text-graphite">{r.version}</span></p>
                    {r.metadata?.description && <p className="max-w-[72ch] text-graphite">{r.metadata.description}</p>}
                    <p className="flex flex-wrap gap-x-4 text-sm text-graphite">
                      <span>{t.labels.id} <Mono value={r.id} /></span>
                      <span>{m.hash} <Mono value={r.content_hash} copy /></span>
                      <span>{f.dateTime(r.created_at)}</span>
                    </p>
                  </div>
                  <Setting on={!!r.enabled}>{r.enabled ? m.listed : m.hidden}</Setting>
                </div>
                {me.permissions.mutate && (
                  <ReasonAction danger={!!r.enabled} label={r.enabled ? m.disable : m.enable} title={r.enabled ? m.disableTitle : m.enableTitle}
                    consequence={r.enabled ? m.disableConsequence : m.enableConsequence}
                    payload={{ release: r.id, enabled: !r.enabled, expected_revision: r.revision }}
                    run={(reason, key) => api.setReleaseEnabled(r.id, { expected_revision: r.revision, enabled: !r.enabled, reason }, key)} onDone={refresh} />
                )}
              </li>
            ))}</ul>
          )}
        </Panel>
      )}
    </Page>
  );
}

/** Catalog text as edited: strings may still be empty. `metadataOf` turns it into what is sent, or nothing when it's blank. */
type MetadataDraft = { title?: string; description?: string; agents?: CatalogAgent[]; i18n?: Record<string, { title?: string; description?: string }> };
const AGENTS: CatalogAgent[] = ['claude', 'codex', 'opencode', 'mcp'];
function metadataOf(d: MetadataDraft): CatalogMetadata | undefined {
  const clean = (v?: string) => (v?.trim() ? v.trim() : undefined);
  const i18n = Object.fromEntries(Object.entries(d.i18n ?? {}).map(([locale, x]) => [locale, { title: clean(x.title), description: clean(x.description) }] as const)
    .map(([locale, x]) => [locale, Object.fromEntries(Object.entries(x).filter(([, v]) => v !== undefined))]).filter(([, x]) => Object.keys(x).length));
  const out = { title: clean(d.title), description: clean(d.description), agents: d.agents?.length ? d.agents : undefined, i18n: Object.keys(i18n).length ? i18n : undefined };
  const present = Object.fromEntries(Object.entries(out).filter(([, v]) => v !== undefined));
  return Object.keys(present).length ? present as CatalogMetadata : undefined;
}

function PublishForm({ onDone }: { onDone: () => Promise<unknown> }) {
  const t = useT(); const m = t.releases; const u = m.fields;
  const [id, setId] = useState(''); const [version, setVersion] = useState('');
  const [source, setSource] = useState(''); const [metadata, setMetadata] = useState<MetadataDraft>({});
  const [metadataJsonOk, setMetadataJsonOk] = useState(true);
  const parsedSource = parseJson(source);
  const catalog = metadataOf(metadata);
  const metadataOk = metadataJsonOk && (catalog === undefined || validCatalogMetadata(catalog));
  const valid = /^[a-z][a-z0-9_-]*$/.test(id) && !!version.trim() && parsedSource.ok && metadataOk;
  const body = { id, version: version.trim(), source: parsedSource.ok ? parsedSource.value : null, ...(catalog ? { metadata: catalog } : {}) };
  const zh = metadata.i18n?.['zh-CN'] ?? {};
  const setZh = (patch: { title?: string; description?: string }) => setMetadata((d) => ({ ...d, i18n: { ...d.i18n, 'zh-CN': { ...d.i18n?.['zh-CN'], ...patch } } }));
  const otherLocales = Object.keys(metadata.i18n ?? {}).filter((l) => l !== 'zh-CN');
  return (
    <ReasonAction label={m.publish} title={m.publishTitle} consequence={m.publishConsequence} valid={valid} payload={body}
      run={(reason, key) => api.publishRelease({ ...body, reason }, key)}
      onDone={async () => { await onDone(); setId(''); setVersion(''); setSource(''); setMetadata({}); }}>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label={u.id} htmlFor="release-id" hint={u.idHint}><Input id="release-id" className="font-mono" autoFocus required value={id} onChange={(e) => setId(e.target.value)} /></Field>
        <Field label={u.version} htmlFor="release-version"><Input id="release-version" required value={version} onChange={(e) => setVersion(e.target.value)} /></Field>
      </div>
      {/* The policy itself is a document, not settings: it is authored in the console's graph editor and arrives here as its exported file. */}
      <JsonArea id="release-source" label={u.source} hint={<>{u.sourceHint} <label className="cursor-pointer text-boundary hover:underline">{u.chooseFile}
        <input type="file" accept="application/json,.json" className="sr-only" onChange={(e) => { const file = e.target.files?.[0]; if (file) void file.text().then(setSource); e.target.value = ''; }} /></label></>}
        value={source} onChange={setSource} rows={8} />
      <fieldset className="grid gap-2">
        <legend className="text-sm font-medium">{u.metadata}</legend>
        <ConfigEditor id="release-metadata-json" value={metadata} onChange={setMetadata} onValidityChange={setMetadataJsonOk} rows={10}
          validate={(v) => (isObject(v) ? null : t.configEditor.notObject)}>
          <div className="grid gap-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label={u.title} htmlFor="release-title" hint={u.titleHint}><Input id="release-title" maxLength={120} value={metadata.title ?? ''} onChange={(e) => setMetadata((d) => ({ ...d, title: e.target.value }))} /></Field>
              <Field label={u.titleZh} htmlFor="release-title-zh"><Input id="release-title-zh" lang="zh-CN" maxLength={120} value={zh.title ?? ''} onChange={(e) => setZh({ title: e.target.value })} /></Field>
              <Field label={u.description} htmlFor="release-description">
                <textarea id="release-description" rows={3} maxLength={1000} value={metadata.description ?? ''} onChange={(e) => setMetadata((d) => ({ ...d, description: e.target.value }))}
                  className="w-full rounded-[var(--radius-control)] border border-rule-strong bg-surface px-2.5 py-1.5" />
              </Field>
              <Field label={u.descriptionZh} htmlFor="release-description-zh">
                <textarea id="release-description-zh" lang="zh-CN" rows={3} maxLength={1000} value={zh.description ?? ''} onChange={(e) => setZh({ description: e.target.value })}
                  className="w-full rounded-[var(--radius-control)] border border-rule-strong bg-surface px-2.5 py-1.5" />
              </Field>
            </div>
            <fieldset className="flex flex-wrap items-center gap-x-5 gap-y-2">
              <legend className="mb-1.5 text-sm text-graphite">{u.agents}</legend>
              {AGENTS.map((a) => (
                <label key={a} className="inline-flex items-center gap-2">
                  <input type="checkbox" className="size-4 accent-[var(--boundary)]" checked={!!metadata.agents?.includes(a)}
                    onChange={(e) => setMetadata((d) => ({ ...d, agents: e.target.checked ? [...(d.agents ?? []), a] : (d.agents ?? []).filter((x) => x !== a) }))} />
                  {u.agentNames[a]}
                </label>
              ))}
            </fieldset>
            {otherLocales.length > 0 && <p className="text-sm text-graphite">{u.otherLocales(otherLocales.join(', '))}</p>}
          </div>
        </ConfigEditor>
        {!metadataOk && metadataJsonOk && <p role="alert" className="text-sm text-deny">{u.metadataInvalid}</p>}
      </fieldset>
    </ReasonAction>
  );
}
